// SizeChartSelect — a field that names a size chart (StoreSizeChart CMS
// content) by its key, with a preview of the chart.
//
// On a product (`field.allowInherit`): "Inherit from category" (`inherit`, the
// default), "No size chart" (`none`) or a chart; inheriting previews the
// chart of the product's first category that has one. On a category
// (TaxonomyNode.sizeChart): no default ("") or a chart.
import React, { useEffect, useMemo, useState } from "react"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { GetLocalizationKeys } from "aldoo-ra/Localization/api"
import SizeChartTable, { tableOfChart } from "aldoo-ra/Store/extensions/size-chart/SizeChartTable"

const INHERIT = "inherit"
const NONE = "none"

let cachedCharts = null

const loadCharts = async () => {
  if (cachedCharts) return cachedCharts
  const res = await GetCMSContent({ match: { "content.type": "StoreSizeChart", deleted: { $ne: true } }, itemsPerPage: 500 })
  // the chart's localized fields (label, note, rowLabels) keep their keys beside the content
  cachedCharts = (res?.data || [])
    .map((doc) => doc.content && { ...doc.content, localization: doc.localization || doc.content.localization || [] })
    .filter((content) => content?.key)
  // the list is read again on the next mount: a chart added meanwhile shows up
  setTimeout(() => (cachedCharts = null), 30000)
  return cachedCharts
}

/** The first of the product's categories (in its order) that names a chart: { name, chart }. */
const loadCategoryDefault = async (slugs) => {
  if (!slugs.length) return null
  const res = await GetCMSContent({ match: { "content.type": "TaxonomyNode", "content.slug": { $in: slugs }, deleted: { $ne: true } }, itemsPerPage: slugs.length })
  const nodes = (res?.data || []).map((doc) => doc.content)
  for (const slug of slugs) {
    const node = nodes.find((entry) => entry?.slug === slug)
    const chart = typeof node?.sizeChart === "string" ? node.sizeChart.trim() : ""
    if (chart && chart !== NONE) return { name: node.name || slug, chart }
  }
  return null
}

const LOCALIZED = ["label", "note", "rowLabels"]

/** A chart's localized fields in `language`: { label?, note?, rowLabels? } — what has no translation is left out. */
const loadTexts = async (chart, language) => {
  const keys = (chart?.localization || []).filter((entry) => LOCALIZED.includes(entry?.field) && entry.localizationKey)
  if (!keys.length || !language) return {}
  const res = await GetLocalizationKeys(language, keys.map((entry) => entry.localizationKey))
  const found = res?.data && typeof res.data === "object" && !Array.isArray(res.data) ? res.data : res || {}
  const texts = {}
  for (const { field, localizationKey } of keys) {
    const text = found[localizationKey]
    // a key with no text in the language reads as {{key}}
    if (text && !(typeof text === "string" && text.startsWith("{{"))) texts[field] = text
  }
  return texts
}

const nameOf = (chart) => (typeof chart?.label === "string" && chart.label ? `${chart.label} (${chart.key})` : chart?.key)

// `language`: the product dialog's — the preview reads in it, as the store page does
export default function SizeChartSelect({ field, value, onChange, parentValues, readOnly = false, language = null }) {
  const allowInherit = !!field?.allowInherit
  const [charts, setCharts] = useState(cachedCharts || [])
  const [inherited, setInherited] = useState(null)
  const selected = typeof value === "string" && value ? value : allowInherit ? INHERIT : ""
  const categories = Array.isArray(parentValues?.categories) ? parentValues.categories.filter((slug) => typeof slug === "string") : []
  const categoriesKey = categories.join(",")

  useEffect(() => {
    let alive = true
    loadCharts()
      .then((list) => alive && setCharts(list))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (!allowInherit || selected !== INHERIT) return
    let alive = true
    loadCategoryDefault(categories)
      .then((found) => alive && setInherited(found))
      .catch(() => alive && setInherited(null))
    return () => {
      alive = false
    }
  }, [allowInherit, selected, categoriesKey])

  const options = useMemo(() => {
    const own = charts.map((chart) => ({ value: chart.key, name: nameOf(chart) }))
    // a key no chart has any more stays shown, so it can be seen and replaced
    const missing = selected && ![INHERIT, NONE].includes(selected) && !charts.some((chart) => chart.key === selected) ? [{ value: selected, name: `${selected} (missing)` }] : []
    const fixed = allowInherit
      ? [
          { value: INHERIT, name: "Inherit from category" },
          { value: NONE, name: "No size chart" },
        ]
      : [{ value: "", name: "No default chart" }]
    return [...fixed, ...missing, ...own]
  }, [charts, allowInherit, selected])

  // "No size chart" names none: there is nothing to show, or to find missing
  const shownKey = selected === INHERIT ? inherited?.chart : selected === NONE ? "" : selected
  const shown = charts.find((chart) => chart.key === shownKey)
  // the chart in the dialog's language: its translations, loaded when the chart or the language changes
  const [texts, setTexts] = useState({})
  useEffect(() => {
    setTexts({})
    if (!shown || !language) return
    let alive = true
    loadTexts(shown, language)
      .then((found) => alive && setTexts(found))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [shown?.key, language])
  const table = shown ? tableOfChart(shown, texts) : null

  // an imported product's chart is the import's: it writes it on every import (a pick here
  // would be undone) — shown, not picked
  const importer = allowInherit ? parentValues?.source?.importer : null

  return (
    <div className="flex flex-col gap-3 w-full" data-testid="size-chart-select">
      {importer ? (
        <div className="flex flex-col" data-testid="size-chart-locked">
          <span className="text-sm text-admin_text">{(options.find((option) => option.value === selected) || options[0])?.name}</span>
          <span className="text-xs text-admin_text_secondary">From the import ({importer})</span>
        </div>
      ) : (
      <DropdownSelect
        variant="admin"
        options={options}
        value={[options.find((option) => option.value === selected) || options[0]]}
        disabled={readOnly}
        enableResetButton={false}
        showCheckbox={false}
        searchEnabled={options.length > 8}
        placeholder="Size chart"
        onChange={(picked) => {
          const option = Array.isArray(picked) ? picked[0] : picked
          if (!option || readOnly) return
          onChange?.(option.value)
        }}
      />
      )}
      {allowInherit && selected === INHERIT && (
        <Typography variant="admin_body2" className="opacity-70 my-0">
          {inherited ? `From the category “${inherited.name}”: ${inherited.chart}` : "None of the product's categories has a default chart (a parent category's may still apply)."}
        </Typography>
      )}
      {shownKey && !shown && charts.length > 0 && (
        <Typography variant="admin_body2" className="text-admin_error my-0">
          There is no size chart “{shownKey}”: the product shows none.
        </Typography>
      )}
      {table && <SizeChartTable table={table} admin />}
      {/* a chart is shared by every product that names it: it is translated where it is made */}
      {table && language && (
        <Typography variant="admin_body2" className="opacity-70 my-0" data-testid="size-chart-language">
          Shown in {String(language).toUpperCase()}. The chart is shared by the products that use it: its names and caption are translated in CMS → Store Size Chart.
        </Typography>
      )}
    </div>
  )
}
